import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, MapPin, Clock } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';
import { INTERVENTION_STATUS, INTERVENTION_TYPES, URGENCY_LEVELS, formatDate } from '../constants';

const Row = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-neutral-100 last:border-0">
    <span className="text-sm text-neutral-500">{label}</span>
    <span className="text-sm font-medium text-neutral-900 text-right">{value || '-'}</span>
  </div>
);

const ClientRequestDetail = () => {
  const { interventionId } = useParams();
  const [it, setIt] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/interventions/${interventionId}`);
      setIt(data);
    } catch (e) { toast.error('Demande introuvable'); } finally { setLoading(false); }
  }, [interventionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !it) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-700" /></div>;
  }
  const st = INTERVENTION_STATUS[it.status] || {};

  return (
    <div className="max-w-2xl" data-testid="client-request-detail">
      <Link to="/espace-client" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Mes demandes
      </Link>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="text-xl font-bold text-neutral-900">{it.number}</h1>
        <span className={`text-sm px-3 py-1 rounded ${st.color}`}>{st.label}</span>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-5 mb-5">
        <Row label="Type" value={INTERVENTION_TYPES[it.type]} />
        <Row label="Urgence" value={URGENCY_LEVELS[it.urgency]?.label} />
        <Row label="Date souhaitée" value={`${it.preferredDate || '-'} ${it.preferredSlot || ''}`} />
        {it.scheduledDate && <Row label="Date planifiée" value={it.scheduledDate} />}
        {it.technicianName && <Row label="Technicien" value={it.technicianName} />}
        <Row label="Adresse" value={<span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{it.address} {it.postalCode} {it.city}</span>} />
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-5 mb-5">
        <p className="text-xs text-neutral-500 mb-1">Description</p>
        <p className="text-neutral-800 whitespace-pre-wrap">{it.description}</p>
        {it.accessInstructions && (
          <>
            <p className="text-xs text-neutral-500 mb-1 mt-4">Consignes d'accès</p>
            <p className="text-neutral-800">{it.accessInstructions}</p>
          </>
        )}
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-5">
        <h2 className="font-bold text-neutral-900 mb-4 flex items-center gap-2"><Clock className="w-4 h-4" />Suivi</h2>
        <div className="space-y-3">
          {(it.history || []).slice().reverse().map((h, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <div className="w-2 h-2 rounded-full bg-red-600 mt-1.5 shrink-0" />
              <div>
                <p className="text-neutral-800">{h.action} {h.newStatus && <span className="text-neutral-500">→ {INTERVENTION_STATUS[h.newStatus]?.label || h.newStatus}</span>}</p>
                <p className="text-xs text-neutral-400">{formatDate(h.at)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientRequestDetail;
